import { Brush } from "lucide-react";
import { Pill, TH, TD } from "../../../../components/ui";
import { CONDITIONS, TOOLS } from "../../../../mock/config.mock";
import { fmtPct } from "../../../../utils/format";
import SectionShell, { SubSection } from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellPct, CellText } from "../cells";

/** Two-column label → number table. rows: [{ key, label, value, onChange, suffix, step }] */
function ValueTable({ rows, readOnly, valueHeader = "Value" }) {
  return (
    <div className="overflow-hidden rounded-[4px] border border-border">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className={TH}>Item</th>
            <th className={`${TH} w-36 text-right`}>{valueHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className={TD}>{r.label}</td>
              <td className={`${TD} py-1`}>
                <CellNumber value={r.value} onChange={r.onChange} disabled={readOnly} step={r.step || "0.05"} min={0} suffix={r.suffix} ariaLabel={r.label} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** id / label / add-hours list (SSPC prep, finish levels). */
function AddHoursTable({ list, onChange, readOnly, name }) {
  return (
    <div className="overflow-hidden rounded-[4px] border border-border">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className={`${TH} w-20`}>Code</th>
            <th className={TH}>Label</th>
            <th className={`${TH} w-36 text-right`}>Adds h / 100 SF</th>
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => (
            <tr key={row.id}>
              <td className={`${TD} font-mono text-[11.5px]`}>{row.id}</td>
              <td className={`${TD} py-1`}>
                <CellText value={row.label} onChange={(v) => onChange(i, "label", v)} disabled={readOnly} ariaLabel={`${name} ${row.id} label`} />
              </td>
              <td className={`${TD} py-1`}>
                <CellNumber value={row.add} onChange={(v) => onChange(i, "add", v)} disabled={readOnly} step="0.05" min={0} ariaLabel={`${name} ${row.id} hours`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FinishingMaskingSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "finishingMasking", onSaved, "Finishing & masking");
  const fm = draft.value;
  if (!fm) return null;

  const setPath = (...path) => (v) =>
    draft.update((d) => {
      let o = d;
      path.slice(0, -1).forEach((k) => { o = o[k]; });
      o[path[path.length - 1]] = v;
    });
  const setListField = (listKey) => (i, field, v) => draft.update((d) => { d[listKey][i][field] = v; });
  const sundriesTotal = fm.sundriesSplit.reduce((s, x) => s + (Number(x.share) || 0), 0);

  return (
    <SectionShell
      title="Finishing & Masking"
      subtitle="Prep, finish-level and masking hours, plus the finishing / caulk / masking / sundries material rules."
      icon={Brush}
      draft={draft}
      readOnly={readOnly}
    >
      <div className="grid gap-6 xl:grid-cols-2">
        <SubSection title="Prep by condition" hint="Hours per 100 SF of surface, by substrate condition.">
          <ValueTable
            readOnly={readOnly}
            valueHeader="h / 100 SF"
            rows={CONDITIONS.map((c) => ({ key: c, label: c, value: fm.conditionPrep[c], onChange: setPath("conditionPrep", c) }))}
          />
        </SubSection>

        <SubSection title="Masking by tool" hint="Spray tools need far more masking and protection.">
          <ValueTable
            readOnly={readOnly}
            valueHeader="h / 100 SF"
            rows={TOOLS.map((t) => ({ key: t, label: t, value: fm.maskRates[t], onChange: setPath("maskRates", t) }))}
          />
        </SubSection>

        <SubSection title="SSPC surface prep" hint="Steel preparation standards — extra hours on top of condition prep.">
          <AddHoursTable list={fm.sspc} onChange={setListField("sspc")} readOnly={readOnly} name="SSPC" />
        </SubSection>

        <SubSection title="Finish levels" hint="Drywall finish level skim / sand hours, billed at the finishing labor rate.">
          <AddHoursTable list={fm.finishLevels} onChange={setListField("finishLevels")} readOnly={readOnly} name="Finish level" />
        </SubSection>

        <SubSection title="Finishing materials (Level 5)">
          <ValueTable
            readOnly={readOnly}
            rows={[
              { key: "cg", label: "Joint compound per 100 SF", value: fm.finishing.compoundGalPer100Sf, onChange: setPath("finishing", "compoundGalPer100Sf"), suffix: "gal" },
              { key: "cb", label: "Compound bucket size", value: fm.finishing.compoundBucketGal, onChange: setPath("finishing", "compoundBucketGal"), suffix: "gal", step: "0.5" },
              { key: "cp", label: "Compound bucket price", value: fm.finishing.compoundBucketPrice, onChange: setPath("finishing", "compoundBucketPrice"), suffix: "$", step: "0.5" },
              { key: "ts", label: "Joint tape coverage per roll", value: fm.finishing.tapeSfPerRoll, onChange: setPath("finishing", "tapeSfPerRoll"), suffix: "SF", step: "50" },
              { key: "tp", label: "Joint tape roll price", value: fm.finishing.tapeRollPrice, onChange: setPath("finishing", "tapeRollPrice"), suffix: "$", step: "0.25" },
            ]}
          />
        </SubSection>

        <div className="flex flex-col gap-6">
          <SubSection title="Caulk">
            <ValueTable
              readOnly={readOnly}
              rows={[
                { key: "tubes", label: "Tubes per 100 LF of joints", value: fm.caulk.tubesPer100Lf, onChange: setPath("caulk", "tubesPer100Lf"), step: "0.5" },
                { key: "price", label: "Price per tube", value: fm.caulk.pricePerTube, onChange: setPath("caulk", "pricePerTube"), suffix: "$", step: "0.25" },
              ]}
            />
          </SubSection>
          <SubSection title="Masking materials">
            <ValueTable
              readOnly={readOnly}
              rows={[
                { key: "tsf", label: "Tape coverage per roll", value: fm.masking.tapeSfPerRoll, onChange: setPath("masking", "tapeSfPerRoll"), suffix: "SF", step: "50" },
                { key: "tp", label: "Tape roll price", value: fm.masking.tapePrice, onChange: setPath("masking", "tapePrice"), suffix: "$", step: "0.25" },
                { key: "psf", label: "Plastic coverage per roll", value: fm.masking.plasticSfPerRoll, onChange: setPath("masking", "plasticSfPerRoll"), suffix: "SF", step: "50" },
                { key: "pp", label: "Plastic roll price", value: fm.masking.plasticPrice, onChange: setPath("masking", "plasticPrice"), suffix: "$", step: "0.5" },
              ]}
            />
          </SubSection>
        </div>

        <SubSection title="Sundries split" hint="How the sundries allowance (Global Assumptions → Sundries %) is broken down on the Material List." className="xl:col-span-2">
          <div className="overflow-hidden rounded-[4px] border border-border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>Category</th>
                  <th className={`${TH} w-36 text-right`}>Share</th>
                </tr>
              </thead>
              <tbody>
                {fm.sundriesSplit.map((s, i) => (
                  <tr key={s.key}>
                    <td className={`${TD} py-1`}>
                      <CellText value={s.label} onChange={(v) => setListField("sundriesSplit")(i, "label", v)} disabled={readOnly} ariaLabel="Sundries category" />
                    </td>
                    <td className={`${TD} py-1`}>
                      <CellPct value={s.share} onChange={(v) => setListField("sundriesSplit")(i, "share", v)} disabled={readOnly} min={0} ariaLabel={`${s.label} share`} />
                    </td>
                  </tr>
                ))}
                <tr className="bg-surface_alt">
                  <td className={`${TD} font-semibold`}>Total</td>
                  <td className={`${TD} text-right`}>
                    {Math.abs(sundriesTotal - 1) < 0.0005 ? (
                      <Pill tone="success">{fmtPct(sundriesTotal)}</Pill>
                    ) : (
                      <Pill tone="warning">{fmtPct(sundriesTotal)} – should be 100%</Pill>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </SubSection>
      </div>
    </SectionShell>
  );
}
